import React, { useRef, useState, useEffect } from 'react';
import { FaCloudUploadAlt, FaUpload, FaCheckCircle } from 'react-icons/fa';

/**
 * Reusable image upload with drag & drop, clipboard paste, preview and validation.
 * Validates: file type (JPG/JPEG only), max size, and exact dimensions.
 *
 * Props:
 *  - onChange(file|null)  → called with a valid File, or null if cleared/invalid
 *  - existingUrl          → current image URL (edit mode), shown until a new file is picked
 *  - requiredWidth/Height → enforced exact dimensions (default 1080×1920)
 *  - maxSizeMB            → max file size (default 10)
 */
const ImageDropzone = ({
    onChange,
    existingUrl = null,
    requiredWidth = 1080,
    requiredHeight = 1920,
    maxSizeMB = 10,
}) => {
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(existingUrl);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    // Only the hovered/focused dropzone handles a paste (avoids conflicts with other instances)
    const activeRef = useRef(false);

    const ALLOWED = ['image/jpeg', 'image/jpg'];

    const validateAndSet = (file) => {
        setError(null);
        setInfo(null);

        if (!ALLOWED.includes(file.type)) {
            setError('Only JPG / JPEG images are allowed.');
            onChange(null);
            return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
            setError(`File too large — max ${maxSizeMB}MB (this one is ${(file.size / (1024 * 1024)).toFixed(2)}MB).`);
            onChange(null);
            return;
        }

        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth !== requiredWidth || img.naturalHeight !== requiredHeight) {
                setError(`Image must be exactly ${requiredWidth}×${requiredHeight}px (got ${img.naturalWidth}×${img.naturalHeight}px).`);
                URL.revokeObjectURL(url);
                onChange(null);
                return;
            }
            setPreview(url);
            setInfo(`${file.name} · ${(file.size / (1024 * 1024)).toFixed(2)}MB · ${img.naturalWidth}×${img.naturalHeight}`);
            onChange(file);
        };
        img.onerror = () => {
            setError('Could not read this image file.');
            URL.revokeObjectURL(url);
            onChange(null);
        };
        img.src = url;
    };

    // Keep a live ref so the global paste listener always uses the latest validator
    const validateRef = useRef(validateAndSet);
    validateRef.current = validateAndSet;

    // Convert any image blob (e.g. a pasted PNG screenshot) to a JPEG File
    const blobToJpegFile = (file) =>
        new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(url);
                        if (!blob) return reject(new Error('convert failed'));
                        resolve(new File([blob], `pasted-${Date.now()}.jpg`, { type: 'image/jpeg' }));
                    },
                    'image/jpeg',
                    0.92
                );
            };
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load failed')); };
            img.src = url;
        });

    // Clipboard paste (Ctrl/Cmd+V) → pick the image, converting non-JPEG to JPEG
    useEffect(() => {
        const onPaste = async (e) => {
            if (!activeRef.current) return; // only the hovered/focused dropzone reacts
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const it of Array.from(items)) {
                if (it.type && it.type.startsWith('image/')) {
                    const f = it.getAsFile();
                    if (!f) return;
                    e.preventDefault();
                    if (f.type === 'image/jpeg' || f.type === 'image/jpg') {
                        validateRef.current(f);
                    } else {
                        try {
                            validateRef.current(await blobToJpegFile(f));
                        } catch (err) {
                            /* ignore */
                        }
                    }
                    return;
                }
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, []);

    const onInput = (e) => {
        const f = e.target.files?.[0];
        if (f) validateAndSet(f);
        e.target.value = ''; // allow re-selecting the same file
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) validateAndSet(f);
    };

    return (
        <div>
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
                onMouseEnter={() => { activeRef.current = true; }}
                onMouseLeave={() => { activeRef.current = false; }}
                onFocus={() => { activeRef.current = true; }}
                onBlur={() => { activeRef.current = false; }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                onDrop={onDrop}
                className={`relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors outline-none
                    ${dragOver
                        ? 'border-main-blue bg-main-blue/5'
                        : error
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300 hover:border-main-blue hover:bg-gray-50'}`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/jpg"
                    className="hidden"
                    onChange={onInput}
                />

                {preview ? (
                    <div className="flex flex-col items-center gap-2">
                        <img
                            src={preview}
                            alt="preview"
                            className="max-h-48 rounded-lg object-contain border border-gray-200 bg-gray-50"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="text-xs text-main-blue font-medium flex items-center gap-1">
                            <FaUpload /> Click, drop, or paste to replace
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2 py-4">
                        <div className="p-3 bg-main-blue/10 rounded-full">
                            <FaCloudUploadAlt className="text-main-blue text-3xl" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">
                            Drag &amp; drop, <span className="text-main-blue">browse</span>, or paste (Ctrl+V)
                        </p>
                        <p className="text-xs text-gray-400">
                            JPG / JPEG · exactly {requiredWidth}×{requiredHeight}px · max {maxSizeMB}MB
                        </p>
                    </div>
                )}
            </div>

            {info && (
                <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                    <FaCheckCircle /> {info}
                </p>
            )}
            {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </div>
    );
};

export default ImageDropzone;
